// The VIZBI Online dates and daily hours in the viewer's time zone (selectedTimeZones.js
// getPreferredTimeZone: the account's last choice, else this browser's, else the browser's zone).
// A keyDates.json item with a session start ("time": "17:00", "timeZone": "Europe/London") is
// moved to the viewer's calendar: in Sydney the Masterclass (3 November, 17:00 London) falls on
// 4 November. Used by the /2026 pages, the banner (useEventState) and #profile's Events tab
// (useAttendedEvents); decided 2026-10-13.

import {computed} from "vue";
import {DateTime} from "luxon";
import history from "@/assets/history.json";
import useUserSafe from "@/composables/useUserSafe";
import {getPreferredTimeZone} from "@/composables/selectedTimeZones";
import {withEarlyBirdDates} from "@/composables/earlyBirdDates";

const year = history.latestYear;
const keyDatesFiles = import.meta.webpackContext("../assets/years", {
  recursive: true,
  regExp: /keyDates\.json$/
});
const RENAMED_CITIES = {Calcutta: "Kolkata", Saigon: "Ho Chi Minh City", Kiev: "Kyiv"};

// the Early-bird's dates come from the fullstack (earlyBirdDates.js), the rest from this site
export function fetchKeyDates(keyYear = year) {
  try {
    return withEarlyBirdDates(keyDatesFiles(`./${keyYear}/keyDates.json`));
  } catch {
    return {};
  }
}

export const getViewerZone = getPreferredTimeZone; // selectedTimeZones.js

// a keyDates item's days, each at its session start in the viewer's zone (none without a time)
// "November 18-19" + 17:00 London → [Nov 18 17:00, Nov 19 17:00] in that zone
export function getLocalDays(item, zone, itemYear = year) {
  const match = String(item?.dates || "").match(/^(\w+)\s+(\d+)(?:-(\d+))?$/);
  if (!match || !item.time || !item.timeZone) return [];
  const [, month, firstDay, lastDay = firstDay] = match;
  const days = [];
  for (let day = Number(firstDay); day <= Number(lastDay); day++) {
    const text = `${month} ${day} ${itemYear} ${item.time}`;
    const start = DateTime.fromFormat(text, "LLLL d yyyy HH:mm", {zone: item.timeZone});
    if (start.isValid) days.push(start.setZone(zone));
  }
  return days;
}

// "4 November", "19–20 November", "30 November–1 December"
export function getLongDates(days) {
  if (!days.length) return "";
  const [first, last] = [days[0], days.at(-1)];
  if (first.hasSame(last, "day")) return first.toFormat("d LLLL");
  if (first.hasSame(last, "month")) return `${first.toFormat("d")}–${last.toFormat("d LLLL")}`;
  return `${first.toFormat("d LLLL")}–${last.toFormat("d LLLL")}`;
}

// keyDates.json's own style: "November 4", "November 19-20" (a range across months: its start)
function getKeyDatesText(days) {
  const [first, last] = [days[0], days.at(-1)];
  if (first.hasSame(last, "day") || !first.hasSame(last, "month")) {
    return first.toFormat("LLLL d");
  }
  return `${first.toFormat("LLLL d")}-${last.toFormat("d")}`;
}

// a group's days in the banner: "4", two days "19 & 20" (2026-10-14), more "19–21"
function getBannerRange(first, last) {
  const lastDay = last.toFormat("d");
  if (first.hasSame(last, "day")) return lastDay;
  const isTwoDays = first.plus({days: 1}).hasSame(last, "day");
  return `${first.toFormat("d")}${isTwoDays ? " & " : "–"}${lastDay}`;
}

// the banner's dates: "Nov 4, 19 & 20" (each group's days; the month again when it changes)
function getBannerDates(groups) {
  let month = "";
  const parts = groups.filter((days) => days.length).map((days) => {
    const [first, last] = [days[0], days.at(-1)];
    const range = getBannerRange(first, last);
    const isNewMonth = first.toFormat("LLL") !== month;
    month = last.toFormat("LLL");
    return isNewMonth ? `${first.toFormat("LLL")} ${range}` : range;
  });
  return parts.join(", ");
}

// the dates on the viewer's calendar differ from the program's (e.g. Sydney, a day later)
export function getIsShifted(item, zone, itemYear = year) {
  const local = getLocalDays(item, zone, itemYear);
  const program = getLocalDays(item, item?.timeZone, itemYear);
  return local.some((day, index) => day.toISODate() !== program[index]?.toISODate());
}

// "Australia/Sydney" → "Sydney", "America/Los_Angeles" → "Los Angeles"
export function getZoneCity(zone) {
  const city = String(zone || "").split("/").pop().replaceAll("_", " ");
  return RENAMED_CITIES[city] || city;
}

// "Sydney local time": shown after dates that differ from the program's (decided 2026-10-13)
export function getLocalNote(zone) {
  return `${getZoneCity(zone)} local time`;
}

// "10:59 pm" or, on a 24-hour browser, "22:59"
function getClockText(time) {
  return is24Hour ? time.toFormat("HH:mm") : time.toFormat("h:mm a").toLowerCase();
}

// a deadline's exact closing time in the viewer's zone, for the Key Dates footnote (2026-10-13):
// "Early-bird registration closes at 10:59 pm Sydney time." The instant never moves (keyDates.json
// time and timeZone: 23:59 anywhere on Earth for early-bird, as useEarlyBird.js)
function getDeadlineFootnote(key, item, zone, itemYear) {
  const [closes] = getLocalDays(item, zone, itemYear);
  if (!closes) return "";
  const name = key.replace(/\s*\(.*\)$/, ""); // "early-bird registration (November)"
  const subject = name.charAt(0).toUpperCase() + name.slice(1);
  return `${subject} closes at ${getClockText(closes)} ${getZoneCity(zone)} time.`;
}

// an extended item's old date (datesOld, struck out in Key Dates) on the viewer's calendar too,
// as its new one (Early-bird, 30 October → 12 November: Seán, 2026-10-29)
function getLocalDatesOld(item, zone, itemYear) {
  const days = getLocalDays({...item, dates: item.datesOld}, zone, itemYear);
  return days.length ? getKeyDatesText(days) : item.datesOld;
}

// keyDates.json with the timed items moved to the viewer's dates; an item's bannerDates covers it
// and the timed items after it (the Masterclass: "Nov 3, 18 & 19"; the Workshop: "Nov 18 & 19")
export function getLocalKeyDates(items, zone, itemYear = year) {
  const timed = Object.keys(items || {}).filter((key) => getLocalDays(items[key], zone).length);
  const local = {...items};
  timed.forEach((key, index) => {
    const days = getLocalDays(items[key], zone, itemYear);
    const groups = timed.slice(index).map((later) => getLocalDays(items[later], zone, itemYear));
    const localNote = getIsShifted(items[key], zone, itemYear) ? getLocalNote(zone) : "";
    local[key] = {...items[key], dates: getKeyDatesText(days), localNote};
    if (items[key].datesOld) local[key].datesOld = getLocalDatesOld(items[key], zone, itemYear);
    if (items[key].isDeadline) {
      local[key].footnote = getDeadlineFootnote(key, items[key], zone, itemYear);
    }
    if (items[key].bannerDates) local[key].bannerDates = getBannerDates(groups);
  });
  return local;
}

// "November 18-19" (or "October 30") → the end of its last day in the zone; null if unreadable
function getItemEnd(item, zone, itemYear) {
  const match = String(item?.dates || "").match(/^(\w+)\s+(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  const [, month, firstDay, lastDay = firstDay] = match;
  const end = DateTime.fromFormat(`${month} ${lastDay} ${itemYear}`, "LLLL d yyyy", {zone});
  return end.isValid ? end.endOf("day") : null;
}

// an entry's first day (its own zone; a deadline: anywhere on Earth), for sorting; null if unreadable
function getItemStart(item, itemYear) {
  const match = String(item?.dates || "").match(/^(\w+)\s+(\d+)/);
  if (!match) return null;
  const zone = item.timeZone || "Etc/GMT+12";
  const start = DateTime.fromFormat(`${match[1]} ${match[2]} ${itemYear}`, "LLLL d yyyy", {zone});
  return start.isValid ? start : null;
}

// in date order (Seán, 2026-10-29: an extended deadline moves past the events before it);
// entries without a readable date last, in their file order
function getSortedEntries(entries, itemYear) {
  const getTime = ([, item]) => getItemStart(item, itemYear)?.toMillis() ?? Infinity;
  return [...entries].sort((first, second) => getTime(first) - getTime(second));
}

// keyDates.json without what's over (e.g. April's dates, once VIZBI Online is next), in date
// order: an entry is kept until the end of its last day (a session: its own zone; a deadline:
// anywhere on Earth, UTC−12, as the early-bird deadline); entries without a readable date are kept
export function getUpcomingKeyDates(items, itemYear = year, now = DateTime.now()) {
  const isUpcoming = ([, item]) => {
    const end = getItemEnd(item, item.timeZone || "Etc/GMT+12", itemYear);
    return !end || end >= now;
  };
  const upcoming = Object.entries(items || {}).filter(isUpcoming);
  return Object.fromEntries(getSortedEntries(upcoming, itemYear));
}

// the browser's clock: 24-hour (e.g. a German browser), else am/pm
const is24Hour = /h2[34]/.test(new Intl.DateTimeFormat(undefined, {hour: "numeric"})
  .resolvedOptions().hourCycle || "");

// "4:00–6:15 am", "10:30 pm–12:45 am" (a range across noon or midnight); 24-hour:
// "17:00–19:15"
function getRange(start, end) {
  if (is24Hour) return `${start.toFormat("HH:mm")}–${end.toFormat("HH:mm")}`;
  const [startHalf, endHalf] = [start, end].map((time) => time.toFormat("a").toLowerCase());
  const [startClock, endClock] = [start, end].map((time) => time.toFormat("h:mm")); // "4:00"
  const startText = startHalf === endHalf ? startClock : `${startClock} ${startHalf}`;
  return `${startText}–${endClock} ${endHalf}`;
}

// "4:00–6:15 am Sydney time", "18:00–20:15 Berlin time"
// the daily session's clock range in the zone: "4:00–6:15 am", "18:00–20:15"
function getSessionRange(item, zone) {
  const [start] = getLocalDays(item, zone);
  if (!start || !item.endTime) return "";
  const [hour, minute] = item.endTime.split(":").map(Number);
  const end = start.setZone(item.timeZone).set({hour, minute}).setZone(zone);
  return getRange(start, end);
}

// how the zone is named after times: "London time" for the program's (not "UK", 2026-10-14),
// else "Sydney local time"
function getTimeName(item, zone) {
  return zone === item?.timeZone ? `${getZoneCity(zone)} time` : getLocalNote(zone);
}

// "4:00–6:15 am Sydney time", "5:00–7:15 pm London time"
function getSessionHours(item, zone) {
  const range = getSessionRange(item, zone);
  if (!range) return "";
  return `${range} ${getZoneCity(zone)} time`;
}

export default function useLocalEventDates() {
  const {user} = useUserSafe();
  const zone = computed(() => getViewerZone(user.value));
  const keyDates = computed(() => getLocalKeyDates(fetchKeyDates(), zone.value));
  // only what's still ahead (the /2026 Key Dates list), judged on the program's own dates
  const upcomingKeyDates = computed(() => {
    return getLocalKeyDates(getUpcomingKeyDates(fetchKeyDates()), zone.value);
  });
  const getDates = (name) => getLongDates(getLocalDays(fetchKeyDates()[name], zone.value));
  const masterclass = computed(() => getDates("VIZBI Online Masterclass"));
  const workshop = computed(() => getDates("VIZBI Online Workshop"));
  // the Program's day the Workshop starts on, as its URL hash: "Nov-19" in Sydney, "Nov-18" in
  // London (the Program dates each day in the zone shown, as here)
  const workshopDayHash = computed(() => {
    const [first] = getLocalDays(fetchKeyDates()["VIZBI Online Workshop"], zone.value);
    return first ? first.setLocale("en").toFormat("LLL-d") : "";
  });
  // "Tuesday, 3 November" in the viewer's zone (the Masterclass page's opening)
  const masterclassDay = computed(() => {
    const [first] = getLocalDays(fetchKeyDates()["VIZBI Online Masterclass"], zone.value);
    return first ? first.setLocale("en").toFormat("cccc, d LLLL") : "";
  });
  const firstEvent = fetchKeyDates()["VIZBI Online Masterclass"];
  const hours = computed(() => getSessionHours(firstEvent, zone.value));
  const hoursRange = computed(() => getSessionRange(firstEvent, zone.value)); // "4:00–6:15 am"
  const timeName = computed(() => getTimeName(firstEvent, zone.value)); // "Sydney local time"
  // "Sydney local time" when the dates shown differ from the program's, else ""
  const localNote = computed(() => {
    return getIsShifted(firstEvent, zone.value) ? getLocalNote(zone.value) : "";
  });
  return {
    zone, keyDates, upcomingKeyDates, masterclass, masterclassDay, workshop, workshopDayHash,
    hours, hoursRange,
    timeName, localNote
  };
}
